"use client";

import React from "react";
import Reveal from "@/components/common/Reveal";
import { ArrowRight, CircleCheck, FileText, ShieldCheck, SlidersHorizontal } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { useLanguage } from "@/hooks/useLanguage";
import { pick, type Lang } from "@/lib/i18n";
import { COMMON } from "@/lib/commonCopy";
interface HeroCopy {
  categoryBadge: string;
  title: string;
  lead: string;
  datasheetsBtn: string;
  imageAlt: string;
  overlayBadge: string;
  cardNote: string;
  cardLink: string;
  specStrip: Array<{
    label: string;
    value: string;
  }>;
}
const copyData: Record<Lang, HeroCopy> = {
  en: {
    categoryBadge: "Wedge Wire Screen Engineering",
    title: "Select the right wedge wire screen for your process.",
    lead: "Configure slot aperture, wire geometry, support rod profiles, and alloy grades to match your specific filtration medium, flow direction, and differential pressure requirements. Sizing spans 0.02 mm to 5.0 mm continuous slots with verified V-wire profiles in AISI 304, 316L, and Duplex 2205.",
    datasheetsBtn: "Screen Specifications",
    imageAlt: "Precision welded stainless steel wedge wire screen cylinder with continuous V-wire slot opening",
    overlayBadge: "V-Wire Welded Construction",
    cardNote: "Cylinders, flat panels, sieve bends & nozzles",
    cardLink: "View Products",
    specStrip: [{
      label: "Slot Opening",
      value: "0.02 mm – 5.0 mm"
    }, {
      label: "Sieve Bend Range",
      value: "0.25 mm – 3.0 mm"
    }, {
      label: "Alloy Grades",
      value: "AISI 304 / 316L / Duplex 2205"
    }, {
      label: "Standards Reference",
      value: "ISO 9044 / ASTM E2016"
    }]
  },
  zh: {
    categoryBadge: "约翰逊楔形网工程选型",
    title: "为您的工艺流程选定合适的楔形丝滤网",
    lead: "根据您的过滤介质特性、流向以及压差要求，精准配置缝隙开孔尺寸、楔形丝截面几何形状、支撑筋结构与合金牌号。产品覆盖 0.02 mm 至 5.0 mm 连续缝隙与多种 V 型丝型材，支持 AISI 304、316L 及双相钢 2205 定制制造。",
    datasheetsBtn: "筛网技术规格",
    imageAlt: "精密电阻焊接不锈钢连续缝隙楔形丝滤筒结构与 V 型截面丝细节",
    overlayBadge: "V型丝焊接结构",
    cardNote: "圆筒滤管、平板筛板、弧形筛及滤帽",
    cardLink: "查看产品",
    specStrip: [{
      label: "缝隙开孔范围",
      value: "0.02 mm – 5.0 mm"
    }, {
      label: "弧形筛缝隙范围",
      value: "0.25 mm – 3.0 mm"
    }, {
      label: "常用合金牌号",
      value: "AISI 304 / 316L / 双相钢 2205"
    }, {
      label: "参考制造标准",
      value: "ISO 9044 / ASTM E2016"
    }]
  },
  es: {
    categoryBadge: "Ingeniería de rejillas de alambre de cuña",
    title: "Seleccione la rejilla de alambre en cuña adecuada para su proceso.",
    lead: "Configure el ancho de ranura, la geometría del perfil, la disposición de varillas de soporte y las aleaciones para su fluido, dirección de flujo y presión diferencial. Ranuras continuas de 0.02 mm a 5.0 mm con perfiles en V verificados en AISI 304, 316L y Duplex 2205.",
    datasheetsBtn: "Especificaciones técnicas",
    imageAlt: "Cilindro de rejilla de alambre de cuña de acero inoxidable soldado con ranura continua",
    overlayBadge: "Construcción soldada V-Wire",
    cardNote: "Cilindros, paneles planos, arcos curvos y toberas",
    cardLink: "Ver productos",
    specStrip: [{
      label: "Abertura de ranura",
      value: "0.02 mm – 5.0 mm"
    }, {
      label: "Rango de criba curva",
      value: "0.25 mm – 3.0 mm"
    }, {
      label: "Aleaciones",
      value: "AISI 304 / 316L / Duplex 2205"
    }, {
      label: "Normas de referencia",
      value: "ISO 9044 / ASTM E2016"
    }]
  },
  de: {
    categoryBadge: "Spaltsieb-Konstruktion & Auslegung",
    title: "Wählen Sie das passende Spaltsieb für Ihren Prozess.",
    lead: "Konfigurieren Sie Spaltweite, Drahtprofilgeometrie, Tragstababstände und Legierungen präzise nach Filtrationsmedium, Durchströmungsrichtung und Differenzdruckanforderungen. Spaltweiten von 0,02 mm bis 5,0 mm mit V-Drahtprofilen in AISI 304, 316L und Duplex 2205.",
    datasheetsBtn: "Technische Daten",
    imageAlt: "Präzisionsgeschweißter Edelstahl-Spaltsiebrohrzylinder mit kontinuierlicher V-Draht-Spaltöffnung",
    overlayBadge: "Geschweißte V-Draht-Bauweise",
    cardNote: "Siebrohre, Flachsiebe, Bogensiebe & Filterdüsen",
    cardLink: "Produkte anzeigen",
    specStrip: [{
      label: "Spaltweite",
      value: "0,02 mm – 5,0 mm"
    }, {
      label: "Bogensieb-Bereich",
      value: "0,25 mm – 3,0 mm"
    }, {
      label: "Legierungen",
      value: "AISI 304 / 316L / Duplex 2205"
    }, {
      label: "Referenznormen",
      value: "ISO 9044 / ASTM E2016"
    }]
  },
  fr: {
    categoryBadge: "Ingénierie des grilles à fil profilé",
    title: "Sélectionnez la grille à fil profilé adaptée à votre procédé.",
    lead: "Configurez l'ouverture de fente, le profil de fil en V, l'armature de support et les nuances d'acier pour répondre à votre fluide, sens d'écoulement et différentiel de pression. Fentes continues de 0,02 mm à 5,0 mm en AISI 304, 316L et Duplex 2205.",
    datasheetsBtn: "Fiches techniques",
    imageAlt: "Cylindre filtrant en fil profilé en coin soudé en acier inoxydable avec fente continue",
    overlayBadge: "Structure soudée en fil en V",
    cardNote: "Cylindres, panneaux plats, grilles courbes et crépines",
    cardLink: "Voir les produits",
    specStrip: [{
      label: "Ouverture de fente",
      value: "0,02 mm – 5,0 mm"
    }, {
      label: "Plage tamis courbé",
      value: "0,25 mm – 3,0 mm"
    }, {
      label: "Nuances d'alliage",
      value: "AISI 304 / 316L / Duplex 2205"
    }, {
      label: "Normes de référence",
      value: "ISO 9044 / ASTM E2016"
    }]
  },
  pt: {
    categoryBadge: "Engenharia de telas de arame em cunha",
    title: "Selecione a tela de arame em cunha ideal para o seu processo.",
    lead: "Configure a abertura da ranhura, o perfil do arame em V, a disposição das barras de suporte e a liga metálica para o seu fluido, direção de fluxo e pressão diferencial. Ranhuras contínuas de 0,02 mm a 5,0 mm em AISI 304, 316L e Duplex 2205.",
    datasheetsBtn: "Especificações técnicas",
    imageAlt: "Cilindro de tela de arame perfilado em cunha soldado em aço inoxidável com ranhura contínua",
    overlayBadge: "Estrutura soldada em arame V",
    cardNote: "Cilindros, painéis planos, telas curvas e bicos",
    cardLink: "Ver produtos",
    specStrip: [{
      label: "Abertura da ranhura",
      value: "0,02 mm – 5,0 mm"
    }, {
      label: "Faixa para tela curva",
      value: "0,25 mm – 3,0 mm"
    }, {
      label: "Ligas metálicas",
      value: "AISI 304 / 316L / Duplex 2205"
    }, {
      label: "Normas de referência",
      value: "ISO 9044 / ASTM E2016"
    }]
  }
};
export default function SelectionHero() {
  const {
    lang
  } = useLanguage();
  const copy = pick(lang, copyData);
  return <section id="wedgewireselectionhero" data-nav="light" className="relative overflow-hidden bg-background mf-hero border-b border-border text-foreground">
      <div className="mf-container relative">
        <div className="grid grid-cols-1 items-center gap-8 lg:grid-cols-12 lg:gap-8">
          {/* Left Column (7 Columns on lg) */}
          <Reveal className="lg:col-span-7 flex flex-col items-start">
            {/* Exactly Two Badges */}
            <div className="mb-4 flex flex-wrap items-center gap-2">
              <Badge variant="outline" className="gap-1.5 rounded-md border-border bg-card px-3 py-1 font-mono mf-caption tracking-tight text-foreground">
                <SlidersHorizontal className="size-3.5 text-primary" aria-hidden="true" />
                <span>{copy.categoryBadge}</span>
              </Badge>
              <Badge variant="outline" className="gap-1.5 rounded-md border-border bg-muted px-3 py-1 font-mono mf-caption text-foreground">
                <ShieldCheck className="size-3.5 text-primary" aria-hidden="true" />
                <span>ISO 9044 / ASTM E2016</span>
              </Badge>
            </div>

            {/* Single Page H1 */}
            <h1 className="font-default text-balance mf-h1 text-foreground">
              {copy.title}
            </h1>

            {/* Lead Paragraph */}
            <p className="mt-4 max-w-2xl text-muted-foreground mf-lead">
              {copy.lead}
            </p>

            {/* CTAs */}
            <div className="mt-6 flex w-full flex-col gap-3 sm:w-auto sm:flex-row sm:items-center">
              <Button size="lg" asChild className="px-6 font-semibold transition-colors">
                <Link to="/RequestAQuotePage" className="inline-flex items-center gap-2">
                  <span>{pick(lang, COMMON.requestQuote)}</span>
                  <ArrowRight className="size-4" aria-hidden="true" />
                </Link>
              </Button>

              <Button variant="outline" size="lg" asChild className="px-6 font-semibold transition-colors">
                <Link to="/WedgeWireScreenPage" className="inline-flex items-center gap-2">
                  <FileText className="size-4" aria-hidden="true" />
                  <span>{copy.datasheetsBtn}</span>
                </Link>
              </Button>
            </div>
          </Reveal>

          {/* Right Column: Hero Card (5 Columns on lg) */}
          <Reveal delay={50} className="lg:col-span-5 w-full">
            <Card className="overflow-hidden rounded-lg border border-border bg-card gap-0 py-0">
              {/* Product Visual Container */}
              <div className="mf-card-media mf-ratio-hero w-full">
                <img src="https://mutualfilter.com/wp-content/uploads/2026/09/wedge_wire-screen.webp" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/wedge_wire_screen_cylinder.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/wedge_wire_screen_cylinder.png-wvc-sizes" alt={copy.imageAlt} loading="eager" fetchPriority="high" decoding="async" className="h-full w-full object-cover object-center" />
                <div className="absolute top-3 left-3 bg-card border border-border px-3 py-1 rounded-md mf-caption font-mono flex items-center gap-1.5 text-foreground">
                  <SlidersHorizontal className="size-3.5 text-primary" aria-hidden="true" />
                  <span>{copy.overlayBadge}</span>
                </div>
              </div>

              {/* Single Footer Line */}
              <CardContent className="p-4">
                <div className="flex items-center justify-between mf-caption">
                  <div className="flex items-center gap-1.5 text-muted-foreground">
                    <CircleCheck className="size-3.5 text-primary shrink-0" aria-hidden="true" />
                    <span>{copy.cardNote}</span>
                  </div>
                  <Link to="/WedgeWireScreenPage" className="font-medium text-primary hover:underline whitespace-nowrap ml-2 flex items-center gap-1 shrink-0">
                    <span>{copy.cardLink}</span>
                    <ArrowRight className="size-3.5" aria-hidden="true" />
                  </Link>
                </div>
              </CardContent>
            </Card>
          </Reveal>
        </div>

        {/* Spec Strip below grid */}
        <div className="mf-spec-strip">
          {copy.specStrip.map((spec, idx) => <div key={idx} className="mf-spec-strip__item">
              <span className="mf-spec-strip__label">{spec.label}</span>
              <span className="mf-spec-strip__value">{spec.value}</span>
            </div>)}
        </div>
      </div>
    </section>;
}